
import { Avatar, Box, Stack, useTheme } from '@mui/material';

import ButtonElement from '@/views/elements/button/ButtonElement';
import classNames from 'classnames/bind';
import { SyntheticEvent } from 'react';
import styles from './HeaderDataFollower.scss';

import { formatDate, getFirstCharacterName, getViewFullInfoByAccountId } from '@/utilities';
import ViewNumberFormat from '@/views/elements/Inputs/ViewNumberFormat';
import RenderRank from './RenderRank';
import TypographyElement from '@/views/elements/typography/TypographyElement';
import { useCosInfo } from '@/controllers/gridManager/accountsManager/accountManager';
import { useTranslation } from 'react-i18next';
import { STATUS_ACCOUNT } from '@/stores/dataStore/memoryStore/const/constIndex';
import useIsMobile from '@/componentShare/hooks/useIsMobile';

const cx = classNames.bind(styles);

export default function HeaderDataFollower({
    info,
    onDelete,
}: {
    info: any
    onDelete?: (obj: any) => void
}) {
    const theme = useTheme()
    const { t } = useTranslation()
    const { cosInfo } = useCosInfo()
    const matches = useIsMobile()

    const { linkaccount, status } = cosInfo

    const handleOnDelete = (e: SyntheticEvent, info: any) => {
        e.stopPropagation()
        onDelete && onDelete(info);
    };


    return (
        <Stack direction={'column'} spacing={2} sx={{ paddingTop: "15px" }}>
            <Stack direction={'column'} spacing={2} sx={{ backgroundColor: theme.palette.background.default }} paddingBottom={2} >
                <Stack direction={matches ? "column" : 'row'} spacing={1}>
                    <Stack direction={'row'} spacing={2} sx={{ width: matches ? "100%" : "50%" }}>
                        <Avatar sx={{ width: 48, height: 48 }} aria-label="recipe" variant="rounded">
                            {getFirstCharacterName(info?.mt_fullname)}
                        </Avatar>

                        <Stack direction={'column'} sx={{ width: "100%" }} >
                            <Stack direction={'row'} spacing={2}>
                                <Stack direction={'column'} >
                                    <Stack direction={'row'} spacing={2} alignItems={'center'}>
                                        <Box>
                                            <TypographyElement FontWeightElement={700} FontSizeElement='18px' ColorElement='primary'>
                                                {info?.mt_fullname}
                                            </TypographyElement>
                                        </Box>
                                        <RenderRank rank={'F'} />
                                    </Stack>

                                    <Box >
                                        <TypographyElement lineHeight={"28px"} FontWeightElement={400} FontSizeElement='14px' ColorElement='secondary'>
                                            {t("copytrade.headerDataFollower.registerDate")} {formatDate(info?.reg_date)}
                                        </TypographyElement>
                                    </Box>
                                </Stack>
                            </Stack>

                            <Stack direction={'column'} >
                                <Box >
                                    <TypographyElement lineHeight={"28px"} FontWeightElement={400} FontSizeElement='14px' ColorElement='secondary'>
                                        {t("copytrade.headerDataFollower.linkAccount")} {getViewFullInfoByAccountId(linkaccount)}
                                    </TypographyElement>
                                </Box>
                            </Stack>

                        </Stack>
                    </Stack>

                    <Box sx={{ borderLeft: `1px solid ${theme.palette.text.secondary}`, paddingRight: "50px" }}>
                    </Box>

                    <Stack direction={matches ? "column" : 'row'} justifyContent={'space-around'} alignItems={'center'} sx={{ width: "100%" }}>
                        <Stack sx={{ width: "100%" }} direction={matches ? "row" : 'column'} justifyContent={matches ? "space-between" : ""} spacing={2}>
                            <Stack direction={'column'} sx={{ width: matches ? "50%" : "100%" }} >
                                <Box>
                                    <TypographyElement FontSizeElement='14px' FontWeightElement={400} ColorElement="secondary" component={'div'}>
                                        {t("copytrade.headerDataFollower.totalCapital")}
                                    </TypographyElement>
                                </Box>
                                <Box>
                                    <ViewNumberFormat fontWeightElement={500} value={info?.total_invest} />
                                </Box>
                            </Stack>
                        </Stack>

                        <Stack sx={{ width: "100%" }} direction={matches ? "row" : 'column'} justifyContent={matches ? "space-between" : ""} spacing={2}>
                            <Stack direction={'column'} sx={{ width: matches ? "50%" : "100%" }} >
                                <Box>
                                    <TypographyElement FontSizeElement='14px' FontWeightElement={400} ColorElement="secondary" component={'div'}>
                                        {t("copytrade.headerDataFollower.netAsset")}
                                    </TypographyElement>
                                </Box>
                                <Box>
                                    <ViewNumberFormat fontWeightElement={500} value={info?.total_nav} displayType='input' />
                                </Box>
                            </Stack>

                        </Stack>

                        <Stack sx={{ width: "100%" }} direction={matches ? "row" : 'column'} justifyContent={matches ? "space-between" : ""} spacing={2}>
                            <Stack direction={'column'} sx={{ width: matches ? "50%" : "100%" }} >
                                <Box>
                                    <TypographyElement FontSizeElement='14px' FontWeightElement={400} ColorElement="secondary" component={'div'}>
                                        {t("copytrade.headerDataFollower.allPnL")}
                                    </TypographyElement>
                                </Box>
                                <Box>
                                    <ViewNumberFormat fontWeightElement={500} value={info?.total_copy_pnl ?? 0} />
                                </Box>
                            </Stack>

                        </Stack>

                        <Stack sx={{ width: "100%" }} direction={matches ? "row" : 'column'} justifyContent={matches ? "space-between" : ""} spacing={2}>
                            <Stack direction={'column'} sx={{ width: matches ? "50%" : "100%" }} >
                                <Box>
                                    <TypographyElement FontSizeElement='14px' FontWeightElement={400} ColorElement="secondary" component={'div'}>
                                        {t("copytrade.portfolioCopies.ppnl")}
                                    </TypographyElement>
                                </Box>
                                <Box>
                                    <ViewNumberFormat fontWeightElement={500} value={info?.total_copy_ar ?? 0} isCheckPNL={true} displayType='text' isPercentage={true} />
                                </Box>
                            </Stack>

                        </Stack>

                        <Stack sx={{ width: "100%" }} direction={matches ? "row" : 'column'} justifyContent={matches ? "space-between" : ""} spacing={2}>
                            <Stack direction={'column'} sx={{ width: matches ? "50%" : "100%" }} >
                                <Box>
                                    <TypographyElement FontSizeElement='14px' FontWeightElement={400} ColorElement="secondary" component={'div'}>
                                        {t("copytrade.headerDataFollower.numberMasterCopy")}
                                    </TypographyElement>
                                </Box>
                                <Box>
                                    <TypographyElement FontSizeElement='14px' FontWeightElement={500} ColorElement="primary" component={'div'}>
                                        {info?.total_copy_order ?? 0}
                                    </TypographyElement>
                                </Box>
                            </Stack>

                        </Stack>

                    </Stack>

                </Stack>

                <Stack direction={'row'} sx={{ width: "100%" }}>
                    <Stack spacing={2} sx={{ width: "100%" }} direction={"row"}>
                        <ButtonElement
                            onClick={(e: SyntheticEvent) => handleOnDelete(e, info)}
                            disabled={status !== STATUS_ACCOUNT.A}
                            customType="Primary"
                            aria-label="add to favorites">
                            {t("copytrade.common.unRegisterFollower")}
                        </ButtonElement>

                    </Stack>
                    <Box sx={{ paddingRight: "50px" }}>
                    </Box>
                </Stack>

            </Stack>
        </Stack>
    );
}
